import { forwardRef, useImperativeHandle, useRef } from "react";

interface EditorPaneProps {
    markdown: string;
    onChange: (value: string) => void;
    showPreview: boolean;
}

export type EditorPaneHandle = {
    insertAtCursor: (text: string) => void;
};

export const EditorPane = forwardRef<EditorPaneHandle, EditorPaneProps>(function EditorPane(
    { markdown, onChange },
    ref,
) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ({
        insertAtCursor(text: string) {
            const el = textareaRef.current;
            const start = el?.selectionStart ?? markdown.length;
            const end = el?.selectionEnd ?? markdown.length;
            const before = markdown.slice(0, start);
            const after = markdown.slice(end);
            const prefix = before.length === 0 || before.endsWith("\n") ? "" : "\n";
            const suffix = after.startsWith("\n") ? "" : "\n";
            const insertion = `${prefix}${text}${suffix}`;
            onChange(before + insertion + after);

            requestAnimationFrame(() => {
                if (!el) return;
                const pos = start + insertion.length;
                el.focus();
                el.setSelectionRange(pos, pos);
            });
        },
    }), [markdown, onChange]);

    return (
        <div className="flex flex-col h-full bg-base-100">
            <div className="bg-base-200/50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-base-content/50 flex justify-between items-center">
                <span>Markdown Input</span>
                <span className="badge badge-sm badge-ghost">Markdown</span>
            </div>
            <div className="flex-1 flex overflow-hidden">
                <textarea
                    ref={textareaRef}
                    className="flex-1 h-full bg-base-100 p-6 resize-none focus:outline-none font-mono text-sm leading-relaxed"
                    placeholder="# Start typing..."
                    value={markdown}
                    onChange={(e) => onChange(e.target.value)}
                    spellCheck={false}
                />
            </div>
        </div>
    );
});
